import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

export default function FireBattleContainer() {
  const [currentAvatarIndex, setCurrentAvatarIndex] = useState(0);
  const [uploadedAvatars, setUploadedAvatars] = useState([]);
  const [userAvatar, setUserAvatar] = useState(null);
  const router = useRouter();
  const { data: session } = useSession();
  
  useEffect(() => {
    fetch('/api/admin/battle-avatars')
      .then(res => res.ok ? res.json() : { avatars: [] })
      .then(data => setUploadedAvatars(data.avatars || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (session?.user?.id) {
      fetch(`/api/profiles/${session.user.id}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.avatar) setUserAvatar(data.avatar);
        })
        .catch(() => {});
    }
  }, [session?.user?.id]);
  
  useEffect(() => {
    if (uploadedAvatars.length === 0) return;
    const interval = setInterval(() => {
      setCurrentAvatarIndex(prev => (prev + 1) % uploadedAvatars.length);
    }, 600);
    return () => clearInterval(interval);
  }, [uploadedAvatars]);

  return (
    <>
      <style>{`
        @keyframes battle-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes battle-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5); }
          50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8); }
        }
        @keyframes ember-float {
          0% { 
            transform: translateY(0) translateX(0) scale(1); 
            opacity: 0.9; 
          }
          100% { 
            transform: translateY(-160px) translateX(10px) scale(0.3); 
            opacity: 0; 
          }
        }
        @keyframes smoke-rise {
          0% { 
            transform: translateY(0) translateX(0) scale(1) rotate(0deg); 
            opacity: 0.35; 
          }
          50% {
            transform: translateY(-60px) translateX(12px) scale(1.6) rotate(8deg);
            opacity: 0.2;
          }
          100% { 
            transform: translateY(-140px) translateX(-8px) scale(2.5) rotate(-5deg); 
            opacity: 0; 
          }
        }
        @keyframes vs-pulse {
          0%, 100% { transform: scale(1); text-shadow: 0 0 20px rgba(250,204,21,0.8); }
          50% { transform: scale(1.1); text-shadow: 0 0 40px rgba(253,224,71,1); }
        }
      `}</style>
      
      <div 
        className="w-[calc(100vw-32px)] md:w-[864px] flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 relative h-[140px] md:h-[180px] hover:scale-[1.02] active:scale-[0.98]"
        style={{
          background: 'linear-gradient(135deg, #020a18 0%, #0a1628 25%, #122240 50%, #0d1a30 75%, #050d1a 100%)',
          border: '2px solid rgba(59, 130, 246, 0.3)',
        }}
        onClick={() => router.push('/battle')}
      >
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(ellipse at center bottom, rgba(59, 130, 246, 0.4) 0%, transparent 60%)',
          }}
        />
        
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          {[...Array(10)].map((_, i) => (
            <div
              key={`smoke-${i}`}
              className="absolute rounded-full"
              style={{
                width: `${20 + (i % 4) * 12}px`,
                height: `${20 + (i % 4) * 12}px`,
                left: `${5 + (i * 9.5)}%`,
                bottom: `${5 + (i * 4) % 20}%`,
                background: 'radial-gradient(circle, rgba(100,100,100,0.4) 0%, rgba(70,70,70,0.2) 50%, transparent 70%)',
                filter: 'blur(6px)',
                animation: `smoke-rise ${3.5 + (i % 3) * 0.8}s linear infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
          {[...Array(25)].map((_, i) => (
            <div
              key={`ember-${i}`}
              className="absolute rounded-full"
              style={{
                width: `${2 + (i % 3) * 2}px`,
                height: `${2 + (i % 3) * 2}px`,
                left: `${2 + (i * 4)}%`,
                bottom: `-5%`,
                background: i % 3 === 0 ? '#93c5fd' : i % 3 === 1 ? '#60a5fa' : '#3b82f6',
                boxShadow: `0 0 ${6 + (i % 3) * 3}px ${i % 3 === 0 ? '#93c5fd' : '#60a5fa'}`,
                animation: `ember-float ${2.5 + (i % 5) * 0.4}s linear infinite`,
                animationDelay: `${(i * 0.12)}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 h-full flex items-center px-3 md:px-6">
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col items-center flex-1">
              <div className="flex flex-col items-center h-[90px] md:h-[110px]">
                <div 
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-yellow-400 shadow-lg bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center overflow-hidden"
                  style={{ animation: 'battle-glow 2s ease-in-out infinite' }}
                >
                  {userAvatar ? (
                    <img src={userAvatar} alt="" className="w-full h-full object-cover" />
                  ) : uploadedAvatars.length > 0 ? (
                    <img 
                      src={uploadedAvatars[(currentAvatarIndex + Math.floor(uploadedAvatars.length / 2)) % uploadedAvatars.length]} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl md:text-3xl">👤</span>
                  )}
                </div>
                <span className="text-white/80 text-[9px] md:text-xs mt-1 uppercase tracking-wide">You</span>
                <div 
                  className="mt-1 px-3 py-1 rounded-lg text-xs md:text-sm font-bold"
                  style={{
                    background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.2) 0%, rgba(34, 197, 94, 0.3) 100%)',
                    border: '1px solid rgba(74, 222, 128, 0.5)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 15px rgba(34, 197, 94, 0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                    color: '#4ade80',
                  }}
                >
                  FREE $10
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center flex-1">
              <div 
                className="text-3xl md:text-5xl font-black text-transparent bg-clip-text"
                style={{ 
                  backgroundImage: 'linear-gradient(180deg, #fef08a 0%, #facc15 50%, #eab308 100%)',
                  animation: 'vs-pulse 1.5s ease-in-out infinite',
                  WebkitBackgroundClip: 'text',
                }}
              >
                VS
              </div>
              <div className="text-yellow-400 text-[9px] md:text-xs font-bold uppercase tracking-wider mt-1">1v1 Battle</div>
              <div className="mt-1.5 flex items-center gap-1 text-white/70 text-[10px] md:text-xs font-medium">
                <span>Tap to Start</span>
                <svg className="w-3 h-3 md:w-3.5 md:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </div>
            </div>

            <div className="flex flex-col items-center flex-1">
              <div className="flex flex-col items-center h-[90px] md:h-[110px]">
                <div 
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-cyan-600 to-blue-900 flex items-center justify-center border-2 border-cyan-400 shadow-lg overflow-hidden"
                  style={{ animation: 'battle-pulse 2s ease-in-out infinite' }}
                >
                  {uploadedAvatars.length > 0 ? (
                    <img 
                      src={uploadedAvatars[currentAvatarIndex]} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl md:text-3xl">👤</span>
                  )}
                </div>
                <div className="flex flex-col items-center mt-0.5">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 animate-bounce" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" transform="rotate(-90 12 12)"/>
                  </svg>
                  <span className="text-yellow-400 text-[10px] md:text-xs font-bold uppercase tracking-wide text-center leading-tight">
                    Battle<br/>Real Players
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
